import { Controller } from '@hotwired/stimulus'

export default class extends Controller {
  static values = {
    templateKey: String,
    previewUrl: String,
    updateUrl: String,
    settings: Object
  }

  static targets = [
    'previewContainer',
    'previewIframe',
    'previewLoading',
    'desktopModeBtn',
    'mobileModeBtn',
    'saveBtn',
    'saveSpinner',
    'lastSaved'
  ]

  connect() {
    this.settings = JSON.parse(JSON.stringify(this.settingsValue)) || {}
    this.previewMode = 'desktop'
    this.previewDebounce = null
    this.hasUnsavedChanges = false

    // Store bound handler so we can properly remove it later
    this.boundBeforeUnloadHandler = this.handleBeforeUnload.bind(this)

    // Initial preview load
    this.refreshPreview()

    // Warn before leaving with unsaved changes
    window.addEventListener('beforeunload', this.boundBeforeUnloadHandler)
  }

  disconnect() {
    window.removeEventListener('beforeunload', this.boundBeforeUnloadHandler)
    if (this.previewDebounce) {
      clearTimeout(this.previewDebounce)
    }
  }

  handleBeforeUnload(event) {
    if (this.hasUnsavedChanges) {
      event.preventDefault()
      event.returnValue = ''
    }
  }

  // ==================== SETTINGS UPDATE ====================

  updateSetting(event) {
    const path = event.target.dataset.settingsPath
    const value = this.getInputValue(event.target)

    this.setNestedValue(this.settings, path, value)
    this.hasUnsavedChanges = true
    this.schedulePreviewRefresh()
  }

  getInputValue(input) {
    if (input.type === 'checkbox') {
      return input.checked
    } else if (input.type === 'number') {
      return parseInt(input.value, 10)
    } else if (input.type === 'radio') {
      return input.value
    } else {
      return input.value
    }
  }

  setNestedValue(obj, path, value) {
    const parts = path.split('.')
    let current = obj

    for (let i = 0; i < parts.length - 1; i++) {
      const part = parts[i]
      if (!current[part] || typeof current[part] !== 'object') {
        current[part] = {}
      }
      current = current[part]
    }

    current[parts[parts.length - 1]] = value
  }

  getNestedValue(obj, path) {
    const parts = path.split('.')
    let current = obj

    for (const part of parts) {
      if (current === null || current === undefined) return undefined
      current = current[part]
    }

    return current
  }

  // ==================== PREVIEW MANAGEMENT ====================

  schedulePreviewRefresh() {
    if (this.previewDebounce) {
      clearTimeout(this.previewDebounce)
    }
    this.previewDebounce = setTimeout(() => this.refreshPreview(), 300)
  }

  refreshPreview() {
    this.showPreviewLoading()

    const url = new URL(this.previewUrlValue, window.location.origin)
    url.searchParams.set('settings', JSON.stringify(this.settings))

    if (this.hasPreviewIframeTarget) {
      this.previewIframeTarget.src = url.toString()
      this.previewIframeTarget.onload = () => {
        this.hidePreviewLoading()
      }
      this.previewIframeTarget.onerror = () => {
        this.hidePreviewLoading()
        this.showToast('Failed to load preview', 'error')
      }
    }
  }

  showPreviewLoading() {
    if (this.hasPreviewLoadingTarget) {
      this.previewLoadingTarget.classList.remove('hidden')
    }
  }

  hidePreviewLoading() {
    if (this.hasPreviewLoadingTarget) {
      this.previewLoadingTarget.classList.add('hidden')
    }
  }

  // ==================== VIEWPORT MODES ====================

  setDesktopMode() {
    this.previewMode = 'desktop'
    this.updateViewportButtons()

    if (this.hasPreviewContainerTarget) {
      this.previewContainerTarget.style.width = '100%'
      this.previewContainerTarget.style.maxWidth = 'none'
    }
  }

  setMobileMode() {
    this.previewMode = 'mobile'
    this.updateViewportButtons()

    if (this.hasPreviewContainerTarget) {
      this.previewContainerTarget.style.width = '375px'
      this.previewContainerTarget.style.maxWidth = '375px'
    }
  }

  updateViewportButtons() {
    if (this.hasDesktopModeBtnTarget && this.hasMobileModeBtnTarget) {
      if (this.previewMode === 'desktop') {
        this.desktopModeBtnTarget.classList.add('active')
        this.mobileModeBtnTarget.classList.remove('active')
      } else {
        this.desktopModeBtnTarget.classList.remove('active')
        this.mobileModeBtnTarget.classList.add('active')
      }
    }
  }

  // ==================== SAVE OPERATIONS ====================

  async save() {
    if (this.hasSaveSpinnerTarget) {
      this.saveSpinnerTarget.classList.remove('hidden')
    }
    if (this.hasSaveBtnTarget) {
      this.saveBtnTarget.disabled = true
    }

    try {
      const csrfToken = document.querySelector('meta[name="csrf-token"]')?.content

      const response = await fetch(this.updateUrlValue, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': csrfToken,
          'Accept': 'application/json'
        },
        body: JSON.stringify({ template_settings: this.settings })
      })

      const data = await response.json()

      if (response.ok && data.success) {
        this.hasUnsavedChanges = false
        this.showToast('Settings saved successfully', 'success')
        this.updateLastSaved()
      } else {
        const errorMsg = data.errors?.join(', ') || 'Failed to save settings'
        this.showToast(errorMsg, 'error')
      }
    } catch (error) {
      console.error('Save error:', error)
      this.showToast('An error occurred while saving', 'error')
    } finally {
      if (this.hasSaveSpinnerTarget) {
        this.saveSpinnerTarget.classList.add('hidden')
      }
      if (this.hasSaveBtnTarget) {
        this.saveBtnTarget.disabled = false
      }
    }
  }

  updateLastSaved() {
    if (this.hasLastSavedTarget) {
      const now = new Date()
      const timeStr = now.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
      this.lastSavedTarget.textContent = `Last saved at ${timeStr}`
      this.lastSavedTarget.classList.remove('hidden')
    }
  }

  // ==================== RESET TO DEFAULTS ====================

  async resetToDefaults() {
    if (!confirm('Are you sure you want to reset all settings to their defaults? This cannot be undone.')) {
      return
    }

    // Clear the settings and save empty object
    this.settings = {}

    try {
      const csrfToken = document.querySelector('meta[name="csrf-token"]')?.content

      const response = await fetch(this.updateUrlValue, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': csrfToken,
          'Accept': 'application/json'
        },
        body: JSON.stringify({ template_settings: {} })
      })

      if (response.ok) {
        this.showToast('Settings reset to defaults', 'success')
        // Reload the page to get fresh default values
        window.location.reload()
      } else {
        this.showToast('Failed to reset settings', 'error')
      }
    } catch (error) {
      console.error('Reset error:', error)
      this.showToast('An error occurred while resetting', 'error')
    }
  }

  // ==================== TOAST NOTIFICATIONS ====================

  showToast(message, type = 'success') {
    const container = document.getElementById('toast-container')
    if (!container) return

    const toast = document.createElement('div')
    toast.className = `toast ${type}`
    toast.innerHTML = `
      <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        ${type === 'success' ?
    '<path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7"/>' :
    '<path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"/>'
}
      </svg>
      <span>${message}</span>
    `

    container.appendChild(toast)

    // Auto-dismiss after 3 seconds
    setTimeout(() => {
      toast.classList.add('hiding')
      setTimeout(() => toast.remove(), 300)
    }, 3000)
  }
}
